import { useMemo } from 'react';
import type { Resource } from '../api/useFetchResources';

export function useResourceFilters(
  resources: Resource[],
  selectedResourceTypes: string[],
  resourceSearch: string,
  typeLoadOrder: string[] = [],
  failedTypes: Set<string> = new Set()
) {
  const uniqueResourceTypes = useMemo(() => {
    const types = resources.map(r => r.type);
    return Array.from(new Set(types)).filter(Boolean);
  }, [resources]);

  const filteredResources = useMemo(() => {
    if (!selectedResourceTypes || selectedResourceTypes.length === 0) return [] as Resource[];

    const filtered = resources.filter(r => selectedResourceTypes.includes(r.type));

    // Sort: failed types first, then by load order (newest first), then by name
    return filtered.sort((a, b) => {
      const aFailed = failedTypes.has(a.type);
      const bFailed = failedTypes.has(b.type);

      // Failed types always come first
      if (aFailed && !bFailed) return -1;
      if (!aFailed && bFailed) return 1;

      // Within same failure status, sort by load order (newest first)
      const aIndex = typeLoadOrder.indexOf(a.type);
      const bIndex = typeLoadOrder.indexOf(b.type);

      if (aIndex !== -1 && bIndex !== -1 && aIndex !== bIndex) {
        return aIndex - bIndex;
      }

      // Within same type, sort by name
      if (a.type !== b.type) return a.type.localeCompare(b.type);
      return a.name.localeCompare(b.name);
    });
  }, [resources, selectedResourceTypes, typeLoadOrder, failedTypes]);

  const searchedResources = useMemo(() => {
    if (!resourceSearch) return filteredResources;
    return filteredResources.filter(r => r.name.toLowerCase().includes(resourceSearch.toLowerCase()));
  }, [filteredResources, resourceSearch]);

  return { uniqueResourceTypes, filteredResources, searchedResources } as const;
}

export default useResourceFilters;
